<script>
export default { name: 'UiTable' };
</script>

<script setup>
import { ref, computed, watch } from 'vue';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-vue-next';

const props = defineProps({
  // Configuración: [{ key, label, align?: 'left'|'center'|'right', headerClass? }]
  // Las celdas se renderizan solas con row[key]; usa el slot `#cell-{key}` para personalizar.
  columns: { type: Array, required: true },
  // Dataset completo (ya filtrado por la vista); la paginación es local.
  rows: { type: Array, default: () => [] },
  rowKey: { type: String, default: 'id' },
  caption: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  loadingText: { type: String, default: 'Cargando datos...' },
  emptyText: { type: String, default: 'No hay registros.' },
  pagination: { type: Boolean, default: true },
  pageSize: { type: Number, default: 10 },
  // Select "registros por página", ej. [10, 25, 50]
  pageSizes: { type: Array, default: null },
  showInfo: { type: Boolean, default: true },
  rowClickable: { type: Boolean, default: false },
  // Función (row) => string con clases CSS extra para cada fila.
  rowClass: { type: Function, default: null },
  // Modo "embebido": sin fondo/borde/esquinas/sombra propios (para acoplarse a una tarjeta contenedora).
  flat: { type: Boolean, default: false },
});

const emit = defineEmits(['update:page', 'update:pageSize', 'row-click']);

const currentPage = ref(1);
const pageSize = ref(props.pageSize);

watch(() => props.pageSize, (v) => { pageSize.value = v; }, { immediate: true });

const totalPages = computed(() => Math.max(1, Math.ceil(props.rows.length / pageSize.value)));
const visibleStart = computed(() =>
  props.rows.length === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1
);
const visibleEnd = computed(() =>
  Math.min(currentPage.value * pageSize.value, props.rows.length)
);

const pagedRows = computed(() => {
  if (!props.pagination) return props.rows;
  return props.rows.slice((currentPage.value - 1) * pageSize.value, currentPage.value * pageSize.value);
});

const showPagination = computed(() => props.pagination && totalPages.value > 1);

// Cuando cambia el volumen de datos (nueva búsqueda/carga) volvemos a la página 1.
watch(() => props.rows.length, () => {
  if (currentPage.value !== 1) {
    currentPage.value = 1;
    emit('update:page', 1);
  }
});

watch(() => pageSize.value, () => {
  currentPage.value = 1;
  emit('update:page', 1);
});

function goToPage(p) {
  const next = Math.min(Math.max(1, p), totalPages.value);
  if (next !== currentPage.value) {
    currentPage.value = next;
    emit('update:page', next);
  }
}

function onRowClick(row) {
  if (props.rowClickable) emit('row-click', row);
}

function onPageSizeChange(event) {
  pageSize.value = Number(event.target.value);
  emit('update:pageSize', pageSize.value);
}

const alignClass = (align) =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '';
</script>

<template>
  <div
    :class="[
      flat
        ? 'bg-transparent border-0 shadow-none rounded-none'
        : 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm',
      'overflow-hidden',
    ]"
  >
    <div class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <caption v-if="caption" class="sr-only">{{ caption }}</caption>

        <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              scope="col"
              :class="['px-6 py-2.5 font-semibold whitespace-nowrap', alignClass(col.align), col.headerClass]"
            >
              <slot :name="`header-${col.key}`" :col="col">{{ col.label }}</slot>
            </th>
          </tr>
        </thead>

        <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
          <tr v-if="loading">
            <td :colspan="columns.length" class="px-6 py-10 text-center text-slate-500">
              <slot name="loading" :colspan="columns.length">
                <span class="inline-flex items-center gap-2 justify-center">
                  <Loader2 class="w-4 h-4 animate-spin" aria-hidden="true" />
                  {{ loadingText }}
                </span>
              </slot>
            </td>
          </tr>

          <tr v-else-if="rows.length === 0">
            <td :colspan="columns.length" class="px-6 py-10 text-center text-slate-400">
              <slot name="empty" :colspan="columns.length">{{ emptyText }}</slot>
            </td>
          </tr>

          <tr
            v-else
            v-for="row in pagedRows"
            :key="row[rowKey]"
            :class="[
              'transition-colors group',
              rowClickable
                ? 'cursor-pointer hover:bg-brand-50/50 dark:hover:bg-brand-900/10'
                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50',
              rowClass ? rowClass(row) : '',
            ]"
            @click="onRowClick(row)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              :class="['px-6 py-4', alignClass(col.align)]"
            >
              <slot :name="`cell-${col.key}`" :row="row" :col="col" :value="row[col.key]">
                <span
                  v-if="row[col.key] !== null && row[col.key] !== undefined && row[col.key] !== ''"
                >
                  {{ row[col.key] }}
                </span>
                <span v-else class="text-slate-300 dark:text-slate-600">--</span>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- PIE DE TABLA: info + paginación -->
    <div
      v-if="showInfo && rows.length > 0 || showPagination"
      class="px-6 py-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-col sm:flex-row items-center justify-between gap-2"
    >
      <p class="text-xs font-semibold text-slate-500 dark:text-slate-400">
        Mostrando
        <span class="font-black text-slate-700 dark:text-slate-200">{{ visibleStart }}-{{ visibleEnd }}</span>
        de {{ rows.length }} registros
      </p>

      <div class="flex items-center gap-2">
        <select
          v-if="pageSizes"
          :value="pageSize"
          aria-label="Registros por página"
          class="px-2 py-1 text-xs font-bold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 focus:outline-none focus:border-brand-500"
          @change="onPageSizeChange"
        >
          <option v-for="s in pageSizes" :key="s" :value="s">{{ s }}/pág.</option>
        </select>

        <nav v-if="showPagination" aria-label="Paginación" class="flex items-center gap-2">
          <button
            type="button"
            :disabled="currentPage <= 1"
            class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-brand-50 hover:text-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Página anterior"
            @click="goToPage(currentPage - 1)"
          >
            <ChevronLeft class="w-3.5 h-3.5" aria-hidden="true" /> Anterior
          </button>
          <span class="px-2 py-0.5 text-xs font-black text-brand-700 dark:text-brand-300">
            {{ currentPage }} / {{ totalPages }}
          </span>
          <button
            type="button"
            :disabled="currentPage >= totalPages"
            class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-brand-50 hover:text-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Página siguiente"
            @click="goToPage(currentPage + 1)"
          >
            Siguiente <ChevronRight class="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>